import sanitizeHtml from 'sanitize-html';

/**
 * Strict HTML allowlist for TipTap-generated chat messages.
 * Only permits tags/attributes that our TipTap editor config and
 * mention-resolution helpers produce. Everything else is stripped.
 */
const CHAT_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [
    // TipTap block elements
    'p',
    'br',
    'blockquote',
    'pre',
    'hr',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    // TipTap inline elements
    'strong',
    'em',
    'u',
    's',
    'del',
    'code',
    // Links
    'a',
    // Mention spans (added by client-side mention resolution)
    'span',
  ],
  allowedAttributes: {
    a: ['href', 'target', 'rel', 'class'],
    span: ['class', 'data-patient-id', 'data-user-id'],
    ol: ['start'],
    pre: ['class'],
    code: ['class'],
    blockquote: ['class'],
    p: ['class'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  disallowedTagsMode: 'discard',
};

/**
 * Sanitize chat message HTML against a strict allowlist.
 * Use server-side before DB storage (primary defense) and
 * client-side before dangerouslySetInnerHTML (defense-in-depth).
 *
 * Mention markers (<@uuid> and <patient:uuid|Name>) look like HTML
 * tags to sanitize-html and would be stripped. We convert them to
 * HTML entities first so they survive sanitization — the client-side
 * resolution functions already handle both raw and escaped forms.
 */
export function sanitizeChatHtml(html: string): string {
  const preserved = html
    .replace(/<@([a-f0-9-]+)>/g, '&lt;@$1&gt;')
    .replace(/<patient:([^|>]+)\|([^>]*)>/g, '&lt;patient:$1|$2&gt;');

  return sanitizeHtml(preserved, CHAT_SANITIZE_OPTIONS);
}

/**
 * Escape HTML special characters to prevent injection when
 * interpolating user-controlled values into HTML strings
 * (e.g. display names in mention tags).
 */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
